import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import Tabs, { TabItem, TabGroup } from 'common/components/tabs/Tabs';
import VehicleSegmentsTab from './components/vehicleSegmentsTab/VehicleSegmentsTab';
import VehicleModelTab from './components/vehicleModelTab/VehicleModelTab';
import VehicleBrandsTab from './components/vehicleBrandsTab/VehicleBrandsTab';
import SuppliersTab from './components/suppliersTab/SuppliersTab';
import GenericTypesTab from './components/genericTypesTab/GenericTypesTab';
import CommunicationsTab from './components/communicationsTab/CommunicationsTab';
import NotificationsTab from './components/notificationsTab/NotificationsTab';
import DamageTypesTab from './components/damageTypesTab/DamageTypesTab';
import MaintenanceComponentsTab from './components/maintenanceComponentsTab/MaintenanceComponentsTab';
import MaintenanceContractTypesTab from './components/maintenanceContractTypesTab/MaintenanceContractTypesTab';
import InsuranceContractTypesTab from './components/insuranceContractTypesTab/InsuranceContractTypesTab';
import ReminderCategoriesTab from './components/reminderCategoriesTab/ReminderCategoriesTab';
import IdentificationTagTab  from './components/identificationTagTab/IdentificationTagTab';
import PrivacyTab from './components/privacyTab/PrivacyTab';
import { useHistory, useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import LocalsTab from './components/localsTab/LocalsTab';
import VehiclesDevicesTab from './components/vehiclesDevicesTab/VehiclesDevicesTab';
import { CompanyDto } from 'api/companies/models/CompanyDto';
import CompaniesService from 'api/companies/CompaniesService';
import ApiTokensTab from './components/apiTokensTab/ApiTokensTab';

const SettingsScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { tabId } = useParams<{ tabId: string }>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehicleSegmentsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_SEGMENTS_READ']);
    const hasVehicleBrandsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_BRANDS_READ']);
    const hasVehicleModelsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_MODELS_READ']);
    const hasGenericTypesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GENERIC_TYPES_READ']);
    const hasSuppliersReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_SUPPLIERS_READ']);
    const hasLocalsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_LOCALS_READ']);
    const hasDiagnosticTypesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_DIAGNOSTIC_TYPES_READ']);
    const hasCommunicationsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COMMUNICATIONS_READ']);
    const hasNotificationsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_NOTIFICATIONS_READ']);
    const hasVehicleDevicesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_READ']);
    const hasReminderCategoriesReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_CATEGORIES_READ']);
    const hasIdentificationTagsReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IDENTIFICATION_TAGS_READ']);
    const hasApiTokensReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_API_TOKENS_READ']);
    const hasPrivacyReadPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_PRIVACY_READ' as any]);

    const [company, setCompany] = useState<CompanyDto | null>();

    const changeTab = (_tabId: string) => {
        history.push(`/settings/${_tabId}/list`);
    }

    const defaultTab =
        hasVehicleSegmentsReadPolicy ? 'segments' :
        hasVehicleBrandsReadPolicy ? 'brands' :
        hasVehicleModelsReadPolicy ? 'models' :
        hasGenericTypesReadPolicy ? 'genericTypes' :
        hasSuppliersReadPolicy ? 'suppliers' :
        hasCommunicationsReadPolicy ? 'communications' :
        hasLocalsReadPolicy ? 'locals' :
        hasNotificationsReadPolicy ? 'notifications' :
        hasVehicleDevicesReadPolicy ? 'vehicleDevices' :
        hasDiagnosticTypesReadPolicy ? 'damageTypes' :
        hasIdentificationTagsReadPolicy ? 'identificationTags' :
        hasReminderCategoriesReadPolicy ? 'reminderCategories' :
        'apiTokens';

    if (!tabId) changeTab(defaultTab);

    const tabGroups: TabGroup[] = useMemo(() => {
        const line1: TabItem[] = [];
        const line2: TabItem[] = [];

        // Linha 1 (ordem alfabética A-M)
        if (hasLocalsReadPolicy) {
            line1.push({ id: 'locals', title: t('settings.locals'), content: <LocalsTab /> });
        }
        if (hasReminderCategoriesReadPolicy) {
            line1.push({ id: 'reminderCategories', title: t('settings.reminder_categories'), content: <ReminderCategoriesTab /> });
        }
        if (hasCommunicationsReadPolicy) {
            line1.push({ id: 'communications', title: t('settings.communications'), content: <CommunicationsTab /> });
        }
        line1.push({ id: 'maintenanceComponents', title: t('settings.maintenance_components'), content: <MaintenanceComponentsTab /> });
        line1.push({ id: 'maintenanceContractTypes', title: t('settings.maintenance_types'), content: <MaintenanceContractTypesTab /> });
        line1.push({ id: 'insuranceContractTypes', title: t('settings.insurance_types'), content: <InsuranceContractTypesTab /> });
        if (hasVehicleDevicesReadPolicy) {
            line1.push({ id: 'vehicleDevices', title: t('settings.vehicle_devices'), content: <VehiclesDevicesTab /> });
        }
        if (hasSuppliersReadPolicy) {
            line1.push({ id: 'suppliers', title: t('settings.suppliers'), content: <SuppliersTab /> });
        }
        if (hasVehicleBrandsReadPolicy) {
            line1.push({ id: 'brands', title: t('settings.vehicle_brands'), content: <VehicleBrandsTab /> });
        }

        // Linha 2 (ordem alfabética M-T)
        if (hasVehicleModelsReadPolicy) {
            line2.push({ id: 'models', title: t('settings.vehicle_models'), content: <VehicleModelTab /> });
        }
        if (hasNotificationsReadPolicy) {
            line2.push({ id: 'notifications', title: t('settings.notifications'), content: <NotificationsTab /> });
        }
        if (hasVehicleSegmentsReadPolicy) {
            line2.push({ id: 'segments', title: t('settings.vehicle_segments'), content: <VehicleSegmentsTab /> });
        }
        if (hasIdentificationTagsReadPolicy) {
            line2.push({ id: 'identificationTags', title: t('settings.identification_tags'), content: <IdentificationTagTab /> });
        }
        if (hasDiagnosticTypesReadPolicy) {
            line2.push({ id: 'damageTypes', title: t('settings.diagnosis_types'), content: <DamageTypesTab /> });
        }
        if (hasGenericTypesReadPolicy) {
            line2.push({ id: 'genericTypes', title: t('settings.generic_types'), content: <GenericTypesTab /> });
        }
        if (hasPrivacyReadPolicy) {
            line2.push({ id: 'privacy', title: t('settings.privacy'), content: <PrivacyTab /> });
        }

        if (hasApiTokensReadPolicy) {
            line2.push({ id: 'apiTokens', title: t('settings.api_tokens'), content: <ApiTokensTab /> });
        }
        

        return [
            { id: 'line1', tabs: line1 },
            { id: 'line2', tabs: line2 }
        ].filter(g => g.tabs.length > 0);
    }, [company, userProfile]);

    useEffect(() => {
        if (userProfile?.companyId) {
            getData();
        }        
    }, [userProfile?.companyId]);

    const getData = async () => {
        setCompany(await CompaniesService.getCurrent());
    }
    
    return (
        <ScreenTitle title={t('settings.title')}>
            <CompactPageLayout title={t('settings.title') as string} contentAreaClassName={compactPageLayoutStyles.scrollableContent}>
                <Tabs
                    groups={tabGroups}
                    activeTabId={tabId}
                    onChange={changeTab}
                />
            </CompactPageLayout>
        </ScreenTitle>
    );
}

export default SettingsScreen;
